/**
 * MESURE : le dessin sur mesure d'un client, et le seul look dont les images ne sont pas ici.
 *
 * LE DESSIN EST UNE DONNEE, PAS DU CODE. Le serveur envoie, pour ce seul compte et avec la page du
 * widget, le texte SVG de chaque etat (repos, attention pour le temps fort, ouvert pour la
 * fermeture) ; ce fichier le pose dans une balise image. Un SVG affiche ainsi n'execute rien et ne
 * charge rien, et ses animations s'arretent quand le visiteur demande moins de mouvement (mesure
 * dans Chrome, reglage du navigateur entier).
 *
 * LE ROBOT DANS LE COIN, LE TEXTE VERS LA PAGE, des deux cotes. L'etiquette est le Message d'accroche
 * du client, rendu dans le lanceur par le cartouche commun : elle se glisse sous le bord du badge qui
 * regarde la page, et file vers le milieu de l'ecran. Le dessin suit une convention de format :
 * 80 x 80, badge centre en (34 ; 46) de 30 de rayon, donc 4 px de marge a sa gauche et 16 a sa droite.
 *
 * UNE IMAGE PAR ETAT, REJOUEE A CHAQUE PASSAGE, SANS RIEN DEMANDER AU RESEAU. Reposer la meme image
 * ne rejoue pas son animation ; une adresse neuve la rejoue. L'adresse est une adresse data: dont
 * seul un commentaire final change : mesure, elle rejoue dans Chrome comme dans WebKit (Safari, iOS),
 * la ou un fragment (#n) ne rejoue pas dans WebKit, et ou une requete neuve (?n) coutait deux
 * requetes au portail toutes les 10 s par visiteur. L'image ne change qu'une fois la suivante
 * chargee : jamais de blanc entre deux etats. L'etat se lit sur l'attribut data-etat, que le modele
 * et la vitrine posent tous deux : un observateur suffit, les gestes n'ont rien a faire de plus.
 *
 * LA PAGE DU CLIENT DOIT ACCEPTER LES IMAGES data: si elle filtre ses images (img-src). Sinon le
 * rond de secours s'affiche, et la console dit pourquoi.
 *
 * LA FERMETURE. L'image ouvert EST la fermeture du client, dessinee dans son style. Sans elle, ou si
 * une image ne charge pas, le lanceur montre un rond a la couleur du client et la croix commune :
 * jamais un bouton vide, et l'avertissement part en console.
 *
 * LA COULEUR DU WIDGET, POUR LE DESSIN QUI LA NOMME. Une image ne lit pas les variables de la page :
 * ce fichier ecrit trois couleurs sur la racine du dessin, sous trois noms reserves que le dessin
 * emploie avec sa propre couleur en repli, par exemple fill: var(--couleur-widget, #1f93ff).
 *   --couleur-widget      la Couleur du Widget, pour ce qui porte la couleur (le badge, la bulle) ;
 *   --couleur-sur-widget  ce qui se dessine sur elle : blanc, ou une encre sombre si elle est pale ;
 *   --encre-widget        la couleur prise comme encre sur du blanc : elle-meme, ou une encre
 *                         sombre quand elle ne s'y lit plus.
 * Le seuil et l'encre sombre sont ceux des croix (couleursDuDessin, dans lanceur.js). Les
 * trois noms vont dans l'attribut style de la racine, que le controle au depot refuse au dessin :
 * ils passent devant ce que sa feuille poserait sur la racine. Un dessin qui ne les nomme pas est
 * pose tel quel, au caractere pres. Une couleur qui n'est pas un code hexadecimal n'est jamais
 * ecrite, puisqu'elle entrerait dans du CSS : le dessin garde les siennes, et la console le dit.
 *
 * CE FICHIER N'IMPORTE RIEN, PAR CONTRAT. Sa feuille est un gabarit de chaine : deux accents graves
 * dans le fichier, pas un de plus.
 */
let moment = 0;

const adresseNeuve = svg => {
  moment += 1;
  return (
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(svg + '<!--' + moment + '-->')
  );
};

const secourir = conteneur => {
  if (conteneur.dataset.secours === 'oui') return;
  conteneur.dataset.secours = 'oui';
  // PAS DE REPLI SILENCIEUX : le site garde un bouton, et la console dit pourquoi.
  // eslint-disable-next-line no-console
  console.warn(
    '[widget] dessin du lanceur introuvable, rond de secours affiche'
  );
};

const NOMS_DE_COULEUR =
  /--(?:couleur-widget|couleur-sur-widget|encre-widget)\b/;
const HEXADECIMAL = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const ecrireLesCouleurs = (svg, couleurs) => {
  const arbre = new DOMParser().parseFromString(svg, 'image/svg+xml');
  const racine = arbre.documentElement;
  // Un texte illisible le serait aussi pour l'image : il part tel quel, et le rond de secours le dit.
  if (arbre.getElementsByTagName('parsererror').length) return svg;
  if (racine.localName !== 'svg') return svg;

  const noms =
    '--couleur-widget:' +
    couleurs.widget +
    ';--couleur-sur-widget:' +
    couleurs.surWidget +
    ';--encre-widget:' +
    couleurs.encre;
  racine.setAttribute(
    'style',
    [racine.getAttribute('style'), noms].filter(Boolean).join(';')
  );
  return new XMLSerializer().serializeToString(arbre);
};

// LE DESSIN DE CHAQUE ETAT, A LA COULEUR DU WIDGET s'il la nomme ; un etat qui ne la nomme pas part
// tel quel. Sans couleur servie, il n'y a rien a suivre : le dessin garde les siennes.
const teindre = (dessin, couleurs) => {
  const nomme = etat =>
    Boolean(dessin[etat]) && NOMS_DE_COULEUR.test(dessin[etat]);
  if (!Object.keys(dessin).some(nomme)) return dessin;
  if (!couleurs || !couleurs.widget) return dessin;

  const lisibles = [couleurs.widget, couleurs.surWidget, couleurs.encre].every(
    couleur => HEXADECIMAL.test(couleur)
  );
  if (!lisibles) {
    // eslint-disable-next-line no-console
    console.warn(
      '[widget] couleur du widget illisible, le dessin du lanceur garde ses couleurs'
    );
    return dessin;
  }

  const teint = {};
  Object.keys(dessin).forEach(etat => {
    teint[etat] = nomme(etat)
      ? ecrireLesCouleurs(dessin[etat], couleurs)
      : dessin[etat];
  });
  return teint;
};

// L'IMAGE DE L'ETAT COURANT, ou celle du repos quand le dessin n'en a pas pour cet etat.
const poserLImage = (conteneur, image, dessin) => {
  const etat = conteneur.dataset.etat;
  const cle = dessin[etat] ? etat : 'repos';
  if (!dessin[cle] || image.dataset.cle === cle) return;

  image.dataset.cle = cle;
  const suivante = new Image();
  suivante.onload = () => {
    if (image.dataset.cle === cle) image.src = suivante.src;
  };
  suivante.onerror = () => secourir(conteneur);
  suivante.src = adresseNeuve(dessin[cle]);
};

export default {
  id: 'mesure',

  css: `
.woot-lanceur[data-look="mesure"] {
  width: auto;
  height: 80px;
  display: flex;
  align-items: flex-end;
  bottom: 16px;
}

/* LE BADGE TOMBE A 20 px DU COIN, comme la bulle classique, et l'etiquette file vers la page : a
   droite, la ligne se range de droite a gauche. Le format laisse 16 px a droite du badge et 4 px a
   sa gauche, d'ou les deux decalages. */
.woot-lanceur[data-look="mesure"].woot-elements--right { right: 4px; flex-direction: row-reverse; }
.woot-lanceur[data-look="mesure"].woot-elements--left { left: 16px; }

/* LE BADGE, DANS SON PROPRE BLOC : la croix et le rond de secours se placent sur lui, quel que soit
   le cote de l'etiquette. */
.woot-lanceur[data-look="mesure"] .woot-mesure__badge {
  position: relative;
  z-index: 1;
  display: block;
  flex: none;
  width: 80px;
  height: 80px;
}

.woot-lanceur[data-look="mesure"] .woot-mesure__dessin {
  display: block;
  width: 100%;
  height: 100%;
  transition: transform .2s ease;
}

.woot-lanceur[data-look="mesure"][data-etat="repos"]:hover .woot-mesure__dessin { transform: translateY(-2px); }

/* LE FOCUS CLAVIER EPOUSE LE DESSIN. Le contour commun suit la boite du lanceur, marges transparentes
   du format comprises, et sortait de l'ecran a droite. Ici une pilule entoure le badge et l'etiquette,
   ou le badge seul quand l'etiquette est repliee. Les memes cotes valent des deux cotes : la marge
   exterieure de l'etiquette egale celle du badge du meme bord (16 px a droite, 4 px a gauche). */
.woot-lanceur[data-look="mesure"][data-focus="oui"] { outline: none; }

.woot-lanceur[data-look="mesure"][data-focus="oui"]::after {
  content: '';
  position: absolute;
  left: -2px;
  right: 10px;
  top: 10px;
  bottom: -2px;
  border: 3px solid var(--client, #1f93ff);
  border-radius: 999px;
  pointer-events: none;
}

/* L'ETIQUETTE : le Message d'accroche, a la couleur du client, glissee sous le badge. Elle reprend
   les mesures validees a l'apercu : 34 px de haut pour un dessin de 80 px, texte gras, centree sur le
   centre du badge. Un texte trop long se coupe proprement, il ne deborde jamais de l'ecran. */
.woot-lanceur[data-look="mesure"] .woot-lanceur__cartouche {
  position: relative;
  left: auto;
  right: auto;
  top: auto;
  z-index: 0;
  flex: none;
  box-sizing: border-box;
  display: block;
  height: 34px;
  max-width: 260px;
  margin: 0 16px 17px -32px;
  padding: 0 16px 0 28px;
  border-radius: 999px;
  background: var(--client, var(--woot-surface));
  color: var(--woot-croix-auto, #fff);
  border: 2px solid #fff;
  box-shadow: 0 10px 22px -12px rgba(0, 0, 0, .55);
  font: 700 14.5px/30px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  transform: none;
}

/* A DROITE, LE MIROIR : l'etiquette passe sous le bord gauche du badge (les 4 px du format, plus
   16 px sous le badge) et garde a l'exterieur les 4 px du badge de ce cote. */
.woot-lanceur[data-look="mesure"].woot-elements--right .woot-lanceur__cartouche {
  margin: 0 -20px 17px 4px;
  padding: 0 28px 0 16px;
}

/* ELLE SE DEROULE DEPUIS LE ROBOT, donc vers la droite a gauche de l'ecran, et vers la gauche a
   droite. La regle du temps fort, plus bas, garde la main pendant le temps fort. */
.woot-lanceur[data-look="mesure"] .woot-lanceur__cartouche.woot-lanceur__cartouche--visible {
  transform: none;
  animation: woot-mesure-deroule .6s cubic-bezier(.2, .8, .2, 1) both;
}

.woot-lanceur[data-look="mesure"].woot-elements--right .woot-lanceur__cartouche.woot-lanceur__cartouche--visible {
  animation-name: woot-mesure-deroule-vers-la-gauche;
}

@keyframes woot-mesure-deroule {
  from { clip-path: inset(0 100% 0 0 round 999px); }
  to { clip-path: inset(0 0 0 0 round 999px); }
}

@keyframes woot-mesure-deroule-vers-la-gauche {
  from { clip-path: inset(0 0 0 100% round 999px); }
  to { clip-path: inset(0 0 0 0 round 999px); }
}

/* LE TEMPS FORT : l'etiquette sautille pendant que le dessin joue son image de temps fort. */
.woot-lanceur[data-look="mesure"][data-etat="attention"] .woot-lanceur__cartouche.woot-lanceur__cartouche--visible {
  animation: woot-mesure-sautille .55s ease-in-out 2;
}

@keyframes woot-mesure-sautille {
  50% { transform: translateY(-4px); }
}

/* OUVERT : l'etiquette se replie, le dessin montre sa fermeture. */
.woot-lanceur[data-look="mesure"][data-etat="ouvert"] .woot-lanceur__cartouche { display: none; }

/* LE ROND DE SECOURS, place exactement sur le badge du dessin. */
.woot-lanceur[data-look="mesure"] .woot-mesure__disque {
  position: absolute;
  left: 4px;
  bottom: 4px;
  z-index: 1;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--client, #1f93ff);
  box-shadow: 0 8px 24px rgba(0, 0, 0, .16);
  display: none;
}

.woot-lanceur[data-look="mesure"] .woot-mesure__bulle {
  position: absolute;
  left: 17px;
  top: 17px;
  width: 26px;
  height: 26px;
  display: none;
}

/* SON ICONE PREND L'ENCRE DE LA CROIX : blanche, ou sombre sur une couleur pale. */
.woot-lanceur[data-look="mesure"] .woot-mesure__bulle path { fill: var(--woot-croix-auto, #fff); }

/* LA CROIX COMMUNE, sur le centre du badge et non sur le centre du lanceur, qui porte l'etiquette. */
.woot-lanceur[data-look="mesure"] .woot-lanceur__croix {
  left: 34px;
  top: auto;
  bottom: 34px;
  margin: 0 0 -11px -11px;
  z-index: 2;
}

.woot-lanceur[data-look="mesure"][data-fermeture="dessin"] .woot-lanceur__croix { display: none; }

.woot-lanceur[data-look="mesure"][data-fermeture="croix"][data-etat="ouvert"] .woot-mesure__dessin { visibility: hidden; }
.woot-lanceur[data-look="mesure"][data-fermeture="croix"][data-etat="ouvert"] .woot-mesure__disque { display: block; }

.woot-lanceur[data-look="mesure"][data-secours="oui"] .woot-mesure__dessin { visibility: hidden; }
.woot-lanceur[data-look="mesure"][data-secours="oui"] .woot-mesure__disque { display: block; }
.woot-lanceur[data-look="mesure"][data-secours="oui"] .woot-mesure__bulle { display: block; }
.woot-lanceur[data-look="mesure"][data-secours="oui"][data-etat="ouvert"] .woot-mesure__bulle { display: none; }
.woot-lanceur[data-look="mesure"][data-secours="oui"] .woot-lanceur__croix { display: block; }

/* SUR MOBILE, L'ETIQUETTE RESTE, plus petite, et ne deborde jamais de l'ecran. */
@media only screen and (max-width: 667px) {
  .woot-lanceur[data-look="mesure"] { height: 72px; }
  .woot-lanceur[data-look="mesure"].woot-elements--right { right: 6px; }
  .woot-lanceur[data-look="mesure"].woot-elements--left { left: 16px; }
  .woot-lanceur[data-look="mesure"] .woot-mesure__badge { width: 72px; height: 72px; }
  .woot-lanceur[data-look="mesure"] .woot-lanceur__cartouche {
    height: 30px;
    max-width: calc(100vw - 120px);
    margin: 0 14px 16px -28px;
    padding: 0 14px 0 24px;
    font-size: 13px;
    line-height: 26px;
  }
  .woot-lanceur[data-look="mesure"].woot-elements--right .woot-lanceur__cartouche { margin: 0 -17px 16px 4px; padding: 0 24px 0 14px; }
  .woot-lanceur[data-look="mesure"] .woot-mesure__disque { left: 4px; bottom: 4px; width: 54px; height: 54px; }
  .woot-lanceur[data-look="mesure"] .woot-mesure__bulle { left: 15px; top: 15px; width: 24px; height: 24px; }
  .woot-lanceur[data-look="mesure"] .woot-lanceur__croix { left: 31px; bottom: 31px; }
  .woot-lanceur[data-look="mesure"][data-focus="oui"]::after { left: -2.4px; right: 8px; top: 8.4px; bottom: -2.4px; }
}
`,

  monter: (conteneur, options) => {
    const dessin = teindre(
      (options && options.lanceur && options.lanceur.drawing) || {},
      options && options.couleurs
    );
    conteneur.dataset.fermeture = dessin.ouvert ? 'dessin' : 'croix';

    const image = document.createElement('img');
    image.className = 'woot-mesure__dessin';
    image.alt = '';
    image.setAttribute('aria-hidden', 'true');
    image.setAttribute('loading', 'eager');
    image.addEventListener('error', () => secourir(conteneur));
    if (dessin.repos) {
      image.dataset.cle = 'repos';
      image.src = adresseNeuve(dessin.repos);
    }

    const disque = document.createElement('span');
    disque.className = 'woot-mesure__disque';
    const bulle = document.createElement('span');
    bulle.className = 'woot-mesure__bulle';
    bulle.innerHTML =
      '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">' +
      '<path d="M4 3h16a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H9l-5 4v-4H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" fill="#fff"/></svg>';
    disque.appendChild(bulle);

    const croix = document.createElement('span');
    croix.className = 'woot-lanceur__croix';

    // Le badge dans son bloc, l'etiquette (posee ensuite par le cartouche commun) a cote de lui.
    const badge = document.createElement('span');
    badge.className = 'woot-mesure__badge';
    badge.appendChild(image);
    badge.appendChild(disque);
    badge.appendChild(croix);
    conteneur.appendChild(badge);

    if (!dessin.repos) {
      secourir(conteneur);
      return;
    }
    new MutationObserver(() => poserLImage(conteneur, image, dessin)).observe(
      conteneur,
      { attributes: true, attributeFilter: ['data-etat'] }
    );
  },

  // TOUT PASSE PAR L'ATTRIBUT D'ETAT, que l'observateur suit : les quatre gestes existent parce que
  // le modele les appelle pour tous les looks. Le mouvement des images s'arrete de lui-meme avec la
  // preference du visiteur, celui de la feuille par la regle commune du SDK.
  gestes: {
    attention: () => {},
    ouvrir: () => {},
    fermer: () => {},
    reduireLeMouvement: () => {},
  },
};
